<template>
  <div class="ArticlePage relative">
    <div
      v-if="isInStoryblokAdmin() && blok.summaryCategories !== '' && $t(`articles.keyword.${blok.summaryCategories}`) === `articles.keyword.${blok.summaryCategories}`"
      class="absolute right-18 top-0 type-sm border border-criticalDark border-b-2 border-r-2 border-r-dark border-b-dark min-w-320 mt-[15px] p-4"
    >
      <!-- warning in storyblok admin -->
      <img
        src="/admin/circle-exclamation-warning.svg"
        class="w-24 float-left mr-12"
      >
      Translation for category is missing.<br>
      Please add <code class="whitespace-nowrap">articles.keyword.{{ blok.summaryCategories }}</code> to translation tool.
    </div>
    <div v-editable="blok" class="w-full overflow-hidden pb-56 desk:pb-64">
      <div class="topMarginReference" />
      <AutoBreadcrumbs v-if="!blok.hideBreadcrumbs" />
      <SpaceHandler
        v-for="(childBlok, index) in blok.body"
        :key="childBlok._uid"
        :blok="childBlok"
      >
        <ProductList
          v-if="childBlok.component === 'ProductList'"
          :blok="childBlok"
          :placement="index"
        />
        <EditorialProductBlock
          v-else-if="childBlok.component === 'EditorialProductBlock'"
          :blok="childBlok"
          :placement="index"
        />
        <StoryblokComponent
          v-else
          :blok="childBlok"
          :placement="index"
        />
      </SpaceHandler>
      <div v-if="articles.length" class="layout-container">
        <div class="px-12 desk:px-32 mb-12 type-headline-lg">
          {{ $t('articles.relatedHeadline') }}
        </div>
        <div class="desk:px-32">
          <client-only>
            <Swiper
              class="swiper-cover desk:!overflow-hidden !overflow-visible w-full"
              :modules="[SwiperA11y]"
              :space-between="uiStore.isMobile ? 4 : 12"
              :loop="false"
              :slides-per-view="uiStore.isMobile ? 2.2 : 3"
              :no-swiping="true"
            >
              <SwiperSlide
                v-for="article in articles"
                :key="article.uuid"
                class="deskLarge:!overflow-hidden desk:cursor-grab desk:active:cursor-grabbing type-headline-sm desk:type-headline-lg"
              >
                <ArticleListingCard
                  :story="article"
                />
              </SwiperSlide>
            </Swiper>
          </client-only>
        </div>
      </div>
    </div>
    <ScrollToTop />
  </div>
</template>

<script setup lang="ts">
import { StoryBlokArticlePage, StoryBlokArticlePageContent } from '#constants/types/storyblok';
import SpaceHandler from '~/components/body/SpaceHandler.vue';
import useSeoCreator from '~/composeables/useSeoCreator';
import ProductList from '~/components/product/ProductList.vue';
import { ISbStoriesParams } from 'storyblok-js-client';
import AutoBreadcrumbs from '~/components/body/AutoBreadcrumbs.vue';
import useStoryblokAdmin from '~/composeables/useStoryblokAdmin';
import { useGlobalContentStore } from '~/store/globalContent';
import { useUiStore } from '~/store/ui';
import ArticleListingCard from '~/storyblok/articles/ArticleListingCard.vue';
import ScrollToTop from '~/components/body/ScrollToTop.vue';

const uiStore = useUiStore();

const storyblokApi = useStoryblokApi();
const { isInStoryblokAdmin } = useStoryblokAdmin();
const globalContent = useGlobalContentStore();
const folder = globalContent.articlesFolder;
const excludedIds = [ globalContent.pageId ];
const articles = ref<StoryBlokArticlePage[]>([]);

const props = defineProps<{
  blok: StoryBlokArticlePageContent,
}>();

const manualIds = (props.blok.articles?.map((i) => {
  if (i.link.story?.id) {
    excludedIds.push(i.link.story?.id);
  }
  return i.link.story?.uuid;
}).filter(Boolean)) || [];

const storyblokPayloadBase = {
  version: useRuntimeConfig().public.storyblokStoriesVersion,
  starts_with: (!isInStoryblokAdmin.value ? globalContent.getStoryblokBasePath :  '') +  folder,
  resolve_links: 'url',
  content_type: 'ArticlePage',
  excluding_ids: excludedIds.join(','),
  sort_by: 'sort_by_date:desc',
} as ISbStoriesParams;

if (props.blok.seo) {
  useSeoCreator().createMeta(props.blok.seo);
}

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
} as any;
if (props.blok.summaryTitle) {
  jsonLd.headline = props.blok.summaryTitle;
}
if (props.blok.summaryImage?.filename) {
  jsonLd.image = [
    props.blok.summaryImage.filename,
  ];
}
if (props.blok.body) {
  const authorBlock = props.blok.body.find((f) => f.component === 'BylineBlock');
  if (authorBlock) {
    jsonLd.author = [ {
      '@type': 'Person',
      'name': authorBlock.name,
      'jobTitle': authorBlock.title,
    } ];
  }
}
useJsonld(jsonLd);

const { data } = await useAsyncData(`loadArticlePage-${props.blok._uid}`, async() => {
  try {
    let loadedSelectedArticles = [];
    if (manualIds.length) {
      const { data } = await storyblokApi.get('cdn/stories/', {
        version: useRuntimeConfig().public.storyblokStoriesVersion as 'draft' | 'published',
        by_uuids: manualIds.join(','),
      });
      if (data?.stories) {
        loadedSelectedArticles = data.stories;
      }
    }
    const remaining = 3 - loadedSelectedArticles.length;
    let loadedAuto = [];
    let serverStoryCount = -1;
    if (remaining > 0) {
      const { data: storyblokData, headers } = await storyblokApi.get('cdn/stories/', {
        ...storyblokPayloadBase,
        per_page: remaining,
        filter_query: {
          summaryCategories: {
            like: props.blok.summaryCategories,
          },
        },
      });
      if (storyblokData) {
        loadedAuto = storyblokData.stories;
        serverStoryCount = parseInt(headers.total ?? storyblokData.stories.length, 10);
      }
    }

    return {
      loadedSelectedArticles,
      loadedAuto,
      serverStoryCount,
    };
  } catch (e) {
    console.error('[ArticlePage] Failed to load related articles:', e);
    return { loadedSelectedArticles: [], loadedAuto: [], serverStoryCount: 0 };
  }
});

watch(data, (val) => {
  if (val) {
    articles.value = [
      ...(val.loadedSelectedArticles || []),
      ...(val.loadedAuto || []),
    ];
  }
}, { immediate: true });
</script>

<style lang="postcss">

</style>
